import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  Menu,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  InputAdornment,
  FormControlLabel,
  Card,
  Drawer,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DeleteOutline as DeleteIcon,
  CheckCircleOutline as CheckIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  InsertDriveFile as FileIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  WarningAmberRounded as WarningIcon,
  ArrowForwardRounded as ArrowForwardIcon,
  CheckCircleRounded as CheckCircleFilledIcon,
  CancelRounded as CancelFilledIcon,
  FileUploadOutlined as FileUploadIcon,
  DescriptionOutlined as TemplatesIcon,
  CheckCircleOutline as ActiveStatIcon,
  HighlightOff as InactiveStatIcon,
  BusinessOutlined as VendorsIcon,
  LayersOutlined as LayersIcon,
  CalendarTodayOutlined as CalendarIcon,
  VisibilityOutlined as ViewIcon,
  DevicesOutlined as DevicesIcon,
} from "@mui/icons-material";
import moment from "moment";
import { toast } from "react-toastify";
import masterConfigIllustration from "../../assets/6202677_3173498 1.svg";
import UploadImg from "../../assets/baseVersion/Upload_img.svg";
import UploadImgDark from "../../assets/Upload_img_dark.svg";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import AgGridTable from "../../components/table/AgGridTable";
import { ColDef } from "ag-grid-community";
import IOSSwitch from "../../components/IOSSwitch";
import MasterConfigDevicesTab from "./MasterConfigDevicesTab";
import { getVendorLogo } from "./vendorLogoUtils";
import eventEmitter from "../../components/utils/eventEmitter";

/** Soft Success/Failed pills matching Config Template redesign. */
const UploadStatusBadge: React.FC<{ label?: string | null; isDarkMode?: boolean }> = ({
  label,
  isDarkMode = false,
}) => {
  const text = String(label || "Unknown");
  const normalized = text.trim().toLowerCase();
  const isSuccess = normalized === "success";
  const isFailed = normalized === "failed";
  const isInProgress = normalized === "processing" || normalized === "queued" || normalized === "queue";

  let color = isDarkMode ? "#9CA3AF" : "#667085";
  let background = isDarkMode ? "rgba(107, 114, 128, 0.15)" : "#F2F4F7";
  let border = isDarkMode ? "1px solid rgba(156, 163, 175, 0.35)" : "1px solid #E4E7EC";
  let Icon: React.ElementType | null = null;

  if (isSuccess) {
    color = "#027A48";
    background = isDarkMode ? "rgba(2, 122, 72, 0.18)" : "#ECFDF3";
    border = "1px solid rgba(2, 122, 72, 0.35)";
    Icon = CheckCircleFilledIcon;
  } else if (isFailed) {
    color = "#B42318";
    background = isDarkMode ? "rgba(180, 35, 24, 0.18)" : "#FEF3F2";
    border = "1px solid rgba(180, 35, 24, 0.35)";
    Icon = CancelFilledIcon;
  } else if (isInProgress) {
    color = "#3A2ADA";
    background = isDarkMode ? "rgba(58, 42, 218, 0.18)" : "#EEF4FF";
    border = "1px solid rgba(58, 42, 218, 0.35)";
  }

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.25,
        height: "26px",
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "12px",
        fontFamily: "Manrope, sans-serif",
        color,
        background,
        border,
        boxSizing: "border-box",
        whiteSpace: "nowrap",
      }}
    >
      {Icon ? <Icon sx={{ fontSize: 15, color }} /> : null}
      {text}
    </Box>
  );
};

const formatUploadedAbsolute = (value: any): string => {
  if (!value) return "-";
  const m = moment(value);
  if (!m.isValid()) return "-";
  return m.format("DD MMM YYYY, HH:mm:ss");
};

const formatUploadedRelative = (value: any): string => {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return "";
  return m.fromNow();
};

const isTemplateUploadFailed = (row: any): boolean => {
  if (!row) return false;
  const label = String(row.status_label ?? "").trim().toLowerCase();
  if (label === "failed") return true;
  const status = row.status;
  if (status === 4 || status === "4") return true;
  return String(status ?? "").trim().toLowerCase() === "failed";
};

const getTemplateActive = (row: any): boolean => {
  if (!row) return false;
  // Failed uploads must never appear as Active.
  if (isTemplateUploadFailed(row)) return false;
  const value = row.active ?? row.is_active;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "active" || normalized === "1" || normalized === "yes";
  }
  return Boolean(value);
};

const isTemplateSuccess = (row: any): boolean => {
  if (!row) return false;
  const label = String(row.status_label ?? "").trim().toLowerCase();
  if (label === "success") return true;
  return row.status === 3 || row.status === "3";
};

/** Asset mapping is allowed only for Active + Success templates. */
const canMapAssetsToTemplate = (row: any): boolean =>
  Boolean(row) && isTemplateSuccess(row) && getTemplateActive(row);

const MAPPING_NOT_READY_MSG =
  "Asset mapping is available only for Active templates with a Success status.";

const ActiveToggleCell: React.FC<{
  params: any;
  isToggling: boolean;
  onRequestToggle: (row: any, nextActive: boolean) => void;
}> = ({ params, isToggling, onRequestToggle }) => {
  const isFailed = isTemplateUploadFailed(params.data);
  const isActive = getTemplateActive(params.data);

  return (
    <Box
      data-prevent-row-click="true"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        gap: 1,
      }}
    >
      <IOSSwitch
        checked={isActive}
        disabled={isToggling || isFailed}
        width={38}
        height={20}
        thumbSize={16}
        onColor="#16A34A"
        offColor="#D1D5DB"
        onChange={(event) => {
          event.stopPropagation();
          if (isFailed) return;
          onRequestToggle(params.data, event.target.checked);
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: "12px",
          fontWeight: 700,
          color: isActive ? "#16A34A" : "#6B7280",
          whiteSpace: "nowrap",
        }}
      >
        {isActive ? "Yes" : "No"}
      </Typography>
    </Box>
  );
};

const TemplateRowActionCell: React.FC<{
  params: any;
  isDarkMode: boolean;
  onViewDetails: (row: any) => void;
  onViewMapped: (row: any) => void;
}> = ({ params, isDarkMode, onViewDetails, onViewMapped }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const mappingReady = canMapAssetsToTemplate(params.data);

  return (
    <Box
      data-prevent-row-click="true"
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
    >
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          color: isDarkMode ? "#9CA3AF" : "#667085",
          "&:hover": { bgcolor: isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)" },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            minWidth: 180,
            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
            borderRadius: "8px",
            bgcolor: isDarkMode ? "#1F2937" : "#FFFFFF",
            color: isDarkMode ? "#F3F4F6" : "#1F2937",
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(null);
            onViewDetails(params.data);
          }}
          sx={{ fontSize: "13px", fontWeight: 500, py: 1, px: 2, gap: 1 }}
        >
          <ViewIcon sx={{ fontSize: 16, color: isDarkMode ? "#9CA3AF" : "#667085" }} />
          View details
        </MenuItem>
        <MenuItem
          disabled={!mappingReady}
          onClick={(e) => {
            e.stopPropagation();
            if (!mappingReady) {
              toast.info(MAPPING_NOT_READY_MSG);
              return;
            }
            setAnchorEl(null);
            onViewMapped(params.data);
          }}
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            py: 1,
            px: 2,
            gap: 1,
            "&.Mui-disabled": { opacity: 0.45 },
          }}
        >
          <DevicesIcon sx={{ fontSize: 16, color: isDarkMode ? "#9CA3AF" : "#667085" }} />
          Mapped assets
        </MenuItem>
      </Menu>
    </Box>
  );
};

const RuleActionCell: React.FC<{ params: any; isDarkMode: boolean; onEditRule?: (rule: any) => void }> = ({ params, isDarkMode, onEditRule }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const rawEditable = params.data?.editable;
  const isEditable =
    rawEditable === true ||
    rawEditable === 1 ||
    String(rawEditable).toLowerCase() === "yes" ||
    String(rawEditable).toLowerCase() === "true" ||
    params.data?.is_editable === true ||
    String(params.data?.is_editable).toLowerCase() === "yes";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isEditable) return;
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleClose();
    if (onEditRule) {
      onEditRule(params.data);
    } else {
      toast.info(`Edit rule: ${params.data?.rule_name || params.data?.parent_cmd || "Selected Rule"}`);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <IconButton
        size="small"
        disabled={!isEditable}
        onClick={handleClick}
        sx={{
          color: isDarkMode ? "#9CA3AF" : "#667085",
          "&.Mui-disabled": {
            color: isDarkMode ? "rgba(156, 163, 175, 0.35)" : "rgba(102, 112, 133, 0.35)",
          },
          "&:hover": isEditable
            ? { bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)" }
            : undefined,
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open && isEditable}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            minWidth: "120px",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
            borderRadius: "8px",
            bgcolor: isDarkMode ? "#1F2937" : "#FFFFFF",
            color: isDarkMode ? "#F3F4F6" : "#1F2937",
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            py: 1,
            px: 2,
            gap: 1,
            "&:hover": {
              bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#F3F4F6",
            },
          }}
        >
          <EditIcon fontSize="small" sx={{ fontSize: "16px", color: isDarkMode ? "#9CA3AF" : "#667085" }} />
          Edit
        </MenuItem>
      </Menu>
    </Box>
  );
};

const MasterConfig: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [templatesList, setTemplatesList] = useState<any[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(false);

  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isDragOverModal, setIsDragOverModal] = useState<boolean>(false);

  // Template Details Drawer state
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateDetails, setTemplateDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<number>(0);
  const [ruleSearchQuery, setRuleSearchQuery] = useState<string>("");

  // Modal Dialog state matching Figma design
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [formName, setFormName] = useState<string>("");
  const [formDescription, setFormDescription] = useState<string>("");
  const [formVendor, setFormVendor] = useState<string>("");
  const [formConnecter, setFormConnecter] = useState<string>("Master Config File");
  const [formCategory, setFormCategory] = useState<string>("");
  const [modalFile, setModalFile] = useState<File | null>(null);

  // Dynamic API state for Vendors and Device Models
  const [vendorsList, setVendorsList] = useState<any[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string>("");
  const [selectedDeviceModelId, setSelectedDeviceModelId] = useState<string>("");
  const [isDownloadingSample, setIsDownloadingSample] = useState<boolean>(false);

  // Edit Rule Dialog state
  const [isEditRuleOpen, setIsEditRuleOpen] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [editScore, setEditScore] = useState<string>("");
  const [editSeverity, setEditSeverity] = useState<number>(2);
  const [editIsActive, setEditIsActive] = useState<boolean>(true);
  const [isSavingRule, setIsSavingRule] = useState<boolean>(false);
  const [togglingTemplateIds, setTogglingTemplateIds] = useState<Set<string>>(new Set());
  const [activeToggleConfirm, setActiveToggleConfirm] = useState<{
    row: any;
    nextActive: boolean;
  } | null>(null);
  const [templateActiveFilter, setTemplateActiveFilter] = useState<"active" | "all">("active");
  const [mappedCountByTemplate, setMappedCountByTemplate] = useState<Record<string, number>>({});
  const [assetsStatusFilter, setAssetsStatusFilter] = useState<"all" | "not_mapped" | "mapped">("all");


  const displayedTemplates = useMemo(() => {
    if (templateActiveFilter === "all") return templatesList;
    // Default "Active" list: exclude Failed uploads and inactive rows.
    return templatesList.filter(
      (row) => !isTemplateUploadFailed(row) && getTemplateActive(row)
    );
  }, [templatesList, templateActiveFilter]);

  const templateListStats = useMemo(() => {
    const total = templatesList.length;
    const active = templatesList.filter((row) => getTemplateActive(row)).length;
    const inactive = total - active;
    const vendors = new Set(
      templatesList.map((t) => String(t.vendor_name || "").trim().toLowerCase()).filter(Boolean)
    ).size;
    return { total, active, inactive, vendors };
  }, [templatesList]);

  const getTemplateDisplayName = (row: any) =>
    row?.name || row?.template_name || row?.file_name || "config";

  const templateNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    templatesList.forEach((t: any) => {
      const id = String(t.template_id || t.id || "");
      if (id) map[id] = getTemplateDisplayName(t);
    });
    return map;
  }, [templatesList]);

  const fetchMappedCounts = async () => {
    try {
      const response = await axiosInstance.get(
        END_POINTS.MASTER_TEMPLATE_DEVICE_MAPPING ||
          "securityscan/master-template/device-mapping",
        { params: { active: true } }
      );
      const rows = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const counts: Record<string, number> = {};
      rows.forEach((m: any) => {
        const tid = String(m.template_id || "");
        if (!tid) return;
        counts[tid] = (counts[tid] || 0) + 1;
      });
      setMappedCountByTemplate(counts);
    } catch (error) {
      console.error("Failed to load template device mapping counts", error);
    }
  };

  const handleRequestToggleTemplateActive = (row: any, nextActive: boolean) => {
    const templateId = row?.template_id || row?.id;
    if (!templateId) {
      toast.error("Missing template ID");
      return;
    }
    if (isTemplateUploadFailed(row)) {
      toast.error("Failed uploads cannot be activated");
      return;
    }
    setActiveToggleConfirm({
      row,
      nextActive,
    });
  };

  const handleCloseActiveToggleConfirm = () => {
    setActiveToggleConfirm(null);
  };

  const handleConfirmToggleTemplateActive = async () => {
    if (!activeToggleConfirm) return;

    const { row, nextActive } = activeToggleConfirm;
    const templateId = row?.template_id || row?.id;
    if (!templateId) {
      toast.error("Missing template ID");
      setActiveToggleConfirm(null);
      return;
    }

    const previousActive = getTemplateActive(row);
    setActiveToggleConfirm(null);

    setTemplatesList((prev) =>
      prev.map((template) =>
        (template.template_id || template.id) === templateId
          ? { ...template, active: nextActive, is_active: nextActive }
          : template
      )
    );
    setTogglingTemplateIds((prev) => {
      const next = new Set(prev);
      next.add(String(templateId));
      return next;
    });

    try {
      // Matches API: /securityscan/master-template/{id}?active=true|false
      const response = await axiosInstance.get(
        `securityscan/master-template/${templateId}?active=${nextActive}`,
        { skipLogoutOn401: true } as any
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(
          response.data?.message ||
            `Template marked as ${nextActive ? "Active" : "Inactive"}`
        );
        // Refresh list after soft activate/deactivate
        await fetchTemplates();
      } else {
        throw new Error(response.data?.message || "Failed to update template status");
      }
    } catch (error: any) {
      setTemplatesList((prev) =>
        prev.map((template) =>
          (template.template_id || template.id) === templateId
            ? { ...template, active: previousActive, is_active: previousActive }
            : template
        )
      );
      console.error("Error updating template active status:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update template status"
      );
    } finally {
      setTogglingTemplateIds((prev) => {
        const next = new Set(prev);
        next.delete(String(templateId));
        return next;
      });
    }
  };

  const getNumericSeverity = (rule: any): number => {
    if (!rule) return 2;
    if (typeof rule.severity === "number" && [1, 2, 3, 4].includes(rule.severity)) {
      return rule.severity;
    }
    const parsed = parseInt(rule.severity, 10);
    if (!isNaN(parsed) && [1, 2, 3, 4].includes(parsed)) {
      return parsed;
    }
    const label = (rule.severity_label || rule.severity || "").toString().toLowerCase();
    if (label.includes("critical")) return 1;
    if (label.includes("high")) return 2;
    if (label.includes("medium")) return 3;
    if (label.includes("low")) return 4;
    return 2;
  };

  const getIsActive = (rule: any): boolean => {
    if (!rule) return true;
    if (typeof rule.is_active === "boolean") return rule.is_active;
    if (typeof rule.is_active === "string") return rule.is_active.toLowerCase() === "true";
    if (typeof rule.active === "boolean") return rule.active;
    if (typeof rule.status === "boolean") return rule.status;
    if (typeof rule.status === "string") return rule.status.toLowerCase() === "active";
    return true;
  };

  const isRuleEditable = (rule: any): boolean => {
    if (!rule) return false;
    const rawEditable = rule.editable;
    return (
      rawEditable === true ||
      rawEditable === 1 ||
      String(rawEditable).toLowerCase() === "yes" ||
      String(rawEditable).toLowerCase() === "true" ||
      rule.is_editable === true ||
      String(rule.is_editable).toLowerCase() === "yes"
    );
  };

  const fetchTemplateDetails = async (templateId: string) => {
    setIsLoadingDetails(true);
    try {
      const [detailsRes, contentRes] = await Promise.allSettled([
        axiosInstance.get(`securityscan/master-template/${templateId}`),
        axiosInstance.get(`securityscan/master-template/${templateId}/content`),
      ]);

      let mergedDetails: any = {};

      if (detailsRes.status === "fulfilled" && (detailsRes.value?.status === 200 || detailsRes.value?.status === 201)) {
        const detailsData = detailsRes.value?.data?.data || detailsRes.value?.data || {};
        mergedDetails = { ...mergedDetails, ...detailsData };
      }

      if (contentRes.status === "fulfilled" && (contentRes.value?.status === 200 || contentRes.value?.status === 201)) {
        const rawContentData = contentRes.value?.data;
        if (typeof rawContentData === "string") {
          mergedDetails.content = rawContentData;
        } else if (rawContentData?.data) {
          if (typeof rawContentData.data === "string") {
            mergedDetails.content = rawContentData.data;
          } else {
            mergedDetails = { ...mergedDetails, ...rawContentData.data };
          }
        } else if (typeof rawContentData === "object" && rawContentData !== null) {
          mergedDetails = { ...mergedDetails, ...rawContentData };
        }
      }

      if (Object.keys(mergedDetails).length > 0) {
        setTemplateDetails(mergedDetails);
      } else {
        toast.error("Failed to load template details");
      }
    } catch (error: any) {
      console.error("Error fetching master template details:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load template details"
      );
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleRowClick = async (rowData: any) => {
    const templateId = rowData?.template_id || rowData?.id;
    if (!templateId) return;

    setSelectedTemplate(rowData);
    setIsDrawerOpen(true);
    setDrawerTab(0);
    setAssetsStatusFilter("all");
    setRuleSearchQuery("");
    setTemplateDetails(null);
    fetchTemplateDetails(templateId);
  };

  const handleMappedDevicesClick = (rowData: any) => {
    const templateId = rowData?.template_id || rowData?.id;
    if (!templateId) return;

    if (!canMapAssetsToTemplate(rowData)) {
      toast.info(MAPPING_NOT_READY_MSG);
      return;
    }

    setSelectedTemplate(rowData);
    setIsDrawerOpen(true);
    setDrawerTab(2);
    setAssetsStatusFilter("mapped");
    setRuleSearchQuery("");
    setTemplateDetails(null);
    fetchTemplateDetails(templateId);
  };

  const handleRulesClick = (rowData: any) => {
    const templateId = rowData?.template_id || rowData?.id;
    if (!templateId) return;

    setSelectedTemplate(rowData);
    setIsDrawerOpen(true);
    setDrawerTab(1);
    setAssetsStatusFilter("all");
    setRuleSearchQuery("");
    setTemplateDetails(null);
    fetchTemplateDetails(templateId);
  };

  const handleMappedDevicesClickRef = useRef(handleMappedDevicesClick);
  handleMappedDevicesClickRef.current = handleMappedDevicesClick;
  const handleRulesClickRef = useRef(handleRulesClick);
  handleRulesClickRef.current = handleRulesClick;
  const handleRowClickRef = useRef(handleRowClick);
  handleRowClickRef.current = handleRowClick;
  const handleRequestToggleTemplateActiveRef = useRef(handleRequestToggleTemplateActive);
  handleRequestToggleTemplateActiveRef.current = handleRequestToggleTemplateActive;

  const handleOpenEditRuleModal = (rule: any) => {
    if (!isRuleEditable(rule)) {
      toast.info("This rule cannot be edited");
      return;
    }
    setEditingRule(rule);
    const scoreVal = rule?.score !== undefined && rule?.score !== null ? String(rule.score) : "0";
    setEditScore(scoreVal);
    setEditSeverity(getNumericSeverity(rule));
    setEditIsActive(getIsActive(rule));
    setIsEditRuleOpen(true);
  };

  const handleCloseEditRuleModal = () => {
    setIsEditRuleOpen(false);
    setEditingRule(null);
  };

  /**
   * Bulk-ready rule updater.
   * Pass one or many rule patches: [{ rule_id, score?, severity?, is_active? }, ...]
   * Single-rule edit and future multi-select bulk save both use this.
   */
  const saveRulesUpdates = async (
    ruleUpdates: Array<Record<string, any>>,
    options?: { successMessage?: string; closeModal?: boolean }
  ) => {
    const templateId =
      templateDetails?.template_id || selectedTemplate?.template_id || selectedTemplate?.id;

    if (!templateId) {
      toast.error("Missing template ID");
      return false;
    }

    if (!ruleUpdates.length) {
      toast.info("No changes were made");
      if (options?.closeModal !== false) handleCloseEditRuleModal();
      return false;
    }

    setIsSavingRule(true);
    try {
      const response = await axiosInstance.patch(
        `securityscan/master-template/${templateId}/rules`,
        { rules: ruleUpdates }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(
          response.data?.message ||
            options?.successMessage ||
            (ruleUpdates.length > 1
              ? `${ruleUpdates.length} rules updated successfully`
              : "Rule updated successfully")
        );
        if (options?.closeModal !== false) {
          handleCloseEditRuleModal();
        }
        await fetchTemplateDetails(templateId);
        return true;
      }

      toast.error(response.data?.message || "Failed to update rule(s)");
      return false;
    } catch (error: any) {
      console.error("Error updating rule(s):", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update rule(s)"
      );
      return false;
    } finally {
      setIsSavingRule(false);
    }
  };

  const handleSaveEditRule = async () => {
    if (!editingRule) return;

    const ruleId = editingRule.rule_id || editingRule.id;
    if (!ruleId) {
      toast.error("Missing rule ID");
      return;
    }

    const scoreNum = parseFloat(editScore);
    if (isNaN(scoreNum) || scoreNum < 0 || scoreNum > 10) {
      toast.error("Score must be a valid number between 0 and 10");
      return;
    }

    const origScore =
      editingRule.score !== undefined && editingRule.score !== null
        ? Number(editingRule.score)
        : null;
    const origSeverity = getNumericSeverity(editingRule);
    const origIsActive = getIsActive(editingRule);

    const changedFields: Record<string, any> = {};

    if (origScore === null || Math.abs(scoreNum - origScore) > 0.0001) {
      changedFields.score = scoreNum;
    }

    if (editSeverity !== origSeverity) {
      changedFields.severity = editSeverity;
    }

    if (editIsActive !== origIsActive) {
      changedFields.is_active = editIsActive;
    }

    if (Object.keys(changedFields).length === 0) {
      toast.info("No changes were made");
      handleCloseEditRuleModal();
      return;
    }

    await saveRulesUpdates([{ rule_id: ruleId, ...changedFields }]);
  };

  const fetchTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const response = await axiosInstance.get("securityscan/master-template/list");
      const list = response.data?.data?.templates || [];
      setTemplatesList(Array.isArray(list) ? list : []);
      await fetchMappedCounts();
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load master templates");
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    const handleTemplateProcessed = (data: any) => {
      console.log('Master template processed event:', data);
      
      const incomingTemplateId = data?.template_id || data?.templateId || data?.id;
      if (!incomingTemplateId) return;
      const normalizedTemplateId = String(incomingTemplateId);

      // Dynamically update the specific template in the list without reloading
      setTemplatesList((prev) => {
        let wasUpdated = false;
        const next = prev.map((template) => {
          const templateId = String(template.template_id || template.id || "");
          if (templateId !== normalizedTemplateId) return template;

          wasUpdated = true;
          return {
            ...template,
            ...data,
            template_id: template.template_id || normalizedTemplateId,
          };
        });

        return wasUpdated ? next : [{ ...data, template_id: normalizedTemplateId }, ...next];
      });

      // Dynamically update the side drawer details if it's currently open
      setTemplateDetails((prevDetails: any) => {
        if (prevDetails && String(prevDetails.template_id || prevDetails.id || "") === normalizedTemplateId) {
           return { ...prevDetails, ...data };
        }
        return prevDetails;
      });

      setSelectedTemplate((prevSelected: any) => {
        if (prevSelected && String(prevSelected.template_id || prevSelected.id || "") === normalizedTemplateId) {
           return { ...prevSelected, ...data };
        }
        return prevSelected;
      });
    };

    eventEmitter.on('master_template_processed', handleTemplateProcessed);
    return () => {
      eventEmitter.off('master_template_processed', handleTemplateProcessed);
    };
  }, []);

  // Fetch vendors from API on mount
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axiosInstance.get(END_POINTS.VENDORS);
        const fetchedVendors = res?.data?.data?.vendors || res?.data?.vendors || [];
        
        // Filter out endpoint vendors
        const hiddenVendors = ["microsoft", "redhat", "debian", "ubuntu", "canonical", "canonical / ubuntu", "huawei", "mikrotik", "edgecore", "tp-link", "tplink", "dell"];
        const filteredVendors = fetchedVendors.filter((v: any) => 
          !hiddenVendors.includes((v.name || "").toLowerCase())
        );
        
        setVendorsList(filteredVendors);
      } catch (err) {
        console.error("Error fetching vendors:", err);
      }
    };
    fetchVendors();
  }, []);

  // Find currently selected vendor object and its device models/categories
  const currentVendorObj = vendorsList.find(
    (v) => v.id === selectedVendorId || v.name === formVendor
  );
  const deviceModelsList: any[] =
    currentVendorObj?.categories ||
    currentVendorObj?.device_models ||
    currentVendorObj?.models ||
    [];

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "number",
        headerName: "#",
        minWidth: 56,
        width: 56,
        flex: 0,
        filter: false,
        sortable: false,
        resizable: true,
        suppressMovable: true,
        suppressMenu: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        menuTabs: [],
        valueGetter: (params: any) =>
          params.node?.rowIndex === null || params.node?.rowIndex === undefined
            ? "-"
            : params.node.rowIndex + 1,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: 0,
          paddingRight: 0,
        },
        headerStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        headerComponent: () => (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: 1,
              overflow: "visible",
              color: isDarkMode ? "#E5E7EB" : "#344054",
            }}
          >
            #
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Template Name",
        flex: 1.3,
        minWidth: 160,
        valueGetter: (params) =>
          params.data?.name || params.data?.template_name || params.data?.file_name || "",
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const name =
            params.value ||
            params.data?.name ||
            params.data?.template_name ||
            params.data?.file_name ||
            "-";
          return (
            <Typography
              title={name}
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: isDarkMode ? "#F3F4F6" : "#101828",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {name}
            </Typography>
          );
        },
      },
      {
        field: "vendor_name",
        headerName: "Vendor",
        flex: 1,
        minWidth: 120,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        },
        cellRenderer: (params: any) => {
          const vendorName = params.data?.vendor_name || params.value || "";
          const logo = getVendorLogo(vendorName, isDarkMode);

          if (logo) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
                title={vendorName}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  style={{
                    maxHeight: 36,
                    maxWidth: 90,
                    objectFit: "contain",
                    objectPosition: "left center",
                    ...(logo.style || {}),
                  }}
                />
              </Box>
            );
          }

          return (
            <Typography
              title={vendorName}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                fontSize: "13px",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {vendorName || "-"}
            </Typography>
          );
        },
      },
      {
        field: "device_model_name",
        headerName: "OS",
        flex: 0.8,
        minWidth: 110,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const os = String(params.value || params.data?.device_model_name || "").trim();
          if (!os) {
            return (
              <Typography sx={{ fontSize: "13px", color: isDarkMode ? "#9CA3AF" : "#98A2B3" }}>
                -
              </Typography>
            );
          }
          return (
            <Typography
              title={os}
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: isDarkMode ? "#E5E7EB" : "#344054",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {os}
            </Typography>
          );
        },
      },
      {
        field: "mapped_devices",
        headerName: "Assets Mapped",
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        filter: false,
        valueGetter: (params: any) => {
          const tid = String(params.data?.template_id || params.data?.id || "");
          return mappedCountByTemplate[tid] || 0;
        },
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const count = Number(params.value || 0);
          const mappingReady = canMapAssetsToTemplate(params.data);
          return (
            <Box
              data-prevent-row-click="true"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!mappingReady) {
                  toast.info(MAPPING_NOT_READY_MSG);
                  return;
                }
                handleMappedDevicesClickRef.current(params.data);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                height: "100%",
                cursor: mappingReady ? "pointer" : "not-allowed",
                opacity: mappingReady ? 1 : 0.55,
              }}
              title={
                mappingReady
                  ? undefined
                  : MAPPING_NOT_READY_MSG
              }
            >
              <LayersIcon sx={{ fontSize: 18, color: isDarkMode ? "#9CA3AF" : "#98A2B3" }} />
              <Box
                sx={{
                  minWidth: 24,
                  height: 24,
                  px: 0.75,
                  borderRadius: "999px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: 700,
                  bgcolor: isDarkMode ? "rgba(58, 42, 218, 0.22)" : "#EEF4FF",
                  color: "#3A2ADA",
                }}
              >
                {count}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "rule_count",
        headerName: "Rules",
        flex: 0.7,
        minWidth: 90,
        sortable: false,
        filter: false,
        valueGetter: (params: any) => Number(params.data?.rule_count ?? 0),
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const count = Number(params.value || 0);
          return (
            <Box
              data-prevent-row-click="true"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleRulesClickRef.current(params.data);
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                height: "100%",
                cursor: "pointer",
              }}
            >
              <Chip
                label={String(count)}
                size="small"
                sx={{
                  height: "22px",
                  fontWeight: 700,
                  fontSize: "12px",
                  bgcolor: count > 0
                    ? (isDarkMode ? "rgba(58, 42, 218, 0.2)" : "#EEF2FF")
                    : (isDarkMode ? "rgba(107,114,128,0.15)" : "#F3F4F6"),
                  color: count > 0 ? "#3A2ADA" : (isDarkMode ? "#9CA3AF" : "#6B7280"),
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: count > 0
                      ? (isDarkMode ? "rgba(58, 42, 218, 0.32)" : "#E0E7FF")
                      : (isDarkMode ? "rgba(107,114,128,0.25)" : "#E5E7EB"),
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "date_added",
        headerName: "Uploaded On",
        flex: 1.35,
        minWidth: 190,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const absolute = formatUploadedAbsolute(params.value);
          const relative = formatUploadedRelative(params.value);
          return (
            <Box sx={{ display: "inline-flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}>
              <CalendarIcon sx={{ fontSize: 16, color: isDarkMode ? "#9CA3AF" : "#98A2B3", mt: "2px" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: isDarkMode ? "#E5E7EB" : "#344054",
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                  }}
                >
                  {absolute}
                </Typography>
                {relative ? (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 500,
                      color: isDarkMode ? "#9CA3AF" : "#667085",
                      lineHeight: 1.3,
                      mt: 0.15,
                    }}
                  >
                    {relative}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      {
        field: "status_label",
        headerName: "Status",
        flex: 0.9,
        minWidth: 120,
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => (
          <UploadStatusBadge label={params.value} isDarkMode={isDarkMode} />
        ),
      },
      {
        field: "active",
        headerName: "Active",
        flex: 0.85,
        minWidth: 120,
        sortable: false,
        filter: false,
        valueGetter: (params: any) => getTemplateActive(params.data),
        cellStyle: { display: "flex", alignItems: "center" },
        cellRenderer: (params: any) => {
          const templateId = String(params.data?.template_id || params.data?.id || "");
          return (
            <ActiveToggleCell
              params={params}
              isToggling={togglingTemplateIds.has(templateId)}
              onRequestToggle={(row, next) => handleRequestToggleTemplateActiveRef.current(row, next)}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        minWidth: 100,
        maxWidth: 110,
        flex: 0,
        pinned: "right",
        lockPosition: "right",
        sortable: false,
        filter: false,
        resizable: false,
        suppressSizeToFit: true,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        cellRenderer: (params: any) => (
          <TemplateRowActionCell
            params={params}
            isDarkMode={isDarkMode}
            onViewDetails={(row) => handleRowClickRef.current(row)}
            onViewMapped={(row) => handleMappedDevicesClickRef.current(row)}
          />
        ),
      },
    ],
    [isDarkMode, togglingTemplateIds, mappedCountByTemplate]
  );

  const rulesDefaultColDef: ColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 100,
      resizable: true,
      sortable: true,
    }),
    []
  );

  const rulesColumnDefs = useMemo<ColDef[]>(
  () => [
    {
      field: "number",
      headerName: "#",
      minWidth: 56,
      width: 56,
      flex: 0,
      filter: false,
      sortable: false,
      resizable: true,
      suppressMovable: true,
      suppressMenu: true,
      suppressHeaderMenuButton: true,
      suppressColumnsToolPanel: true,
      menuTabs: [],
      valueGetter: (params: any) =>
        params.node?.rowIndex === null || params.node?.rowIndex === undefined
          ? "-"
          : params.node.rowIndex + 1,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingLeft: 0,
        paddingRight: 0,
      },
      headerStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      headerComponent: () => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "13px",
            lineHeight: 1,
            overflow: "visible",
            color: isDarkMode ? "#E5E7EB" : "#344054",
          }}
        >
          #
        </Box>
      ),
    },
    {
      field: "rule_name",
      headerName: "Rule Name",
      flex: 3,
      minWidth: 320,
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      valueGetter: (params: any) => params.data?.rule_name || params.data?.parent_cmd || "",
      cellRenderer: (params: any) => {
        const val = params.value || "";
        return (
          <Tooltip title={val} arrow placement="top-start">
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: isDarkMode ? "#E0E0E0" : "#1F2937",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "default",
                lineHeight: 1.2,
                display: "block",
                maxWidth: "100%",
              }}
            >
              {val}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "rule_type_label",
      headerName: "Rule Type",
      flex: 1,
      minWidth: 120,
      hide: true,
      cellRenderer: (params: any) => (
        <Chip
          label={params.value || "Leaf"}
          size="small"
          sx={{
            height: "22px",
            fontSize: "11px",
            fontWeight: 600,
            bgcolor: isDarkMode ? "#1F2937" : "#F3F4F6",
            color: isDarkMode ? "#D1D5DB" : "#4B5563",
          }}
        />
      ),
    },
    {
      field: "mapping_status_label",
      headerName: "Mapping Status",
      flex: 0,
      width: 140,
      minWidth: 130,
      maxWidth: 150,
      cellRenderer: (params: any) => {
        const isMapped = (params.value || "").toLowerCase() === "mapped";
        return (
          <Chip
            label={params.value || "Unmapped"}
            size="small"
            sx={{
              height: "22px",
              fontSize: "11px",
              fontWeight: 600,
              bgcolor: isMapped
                ? (isDarkMode ? "rgba(34, 197, 94, 0.12)" : "#F0FDF4")
                : (isDarkMode ? "rgba(107, 114, 128, 0.12)" : "#F3F4F6"),
              color: isMapped ? "#16A34A" : (isDarkMode ? "#9CA3AF" : "#6B7280"),
              border: `1px solid ${isMapped ? "rgba(34, 197, 94, 0.3)" : "rgba(107, 114, 128, 0.3)"}`,
            }}
          />
        );
      },
    },
    {
      field: "score",
      headerName: "Score",
      flex: 0,
      width: 90,
      minWidth: 80,
      maxWidth: 100,
      valueFormatter: (params: any) => (params.value !== undefined && params.value !== null ? params.value : "-"),
    },
    {
      field: "severity_label",
      headerName: "Severity",
      flex: 0,
      width: 120,
      minWidth: 110,
      maxWidth: 130,
      cellRenderer: (params: any) => {
        const sev = (params.value || "").toLowerCase();
        let color = "#3B82F6";
        let bg = isDarkMode ? "rgba(59, 130, 246, 0.15)" : "#EFF6FF";
        let border = "rgba(59, 130, 246, 0.3)";

        if (sev === "critical") {
          color = "#EF4444";
          bg = isDarkMode ? "rgba(239, 68, 68, 0.15)" : "#FEF2F2";
          border = "rgba(239, 68, 68, 0.3)";
        } else if (sev === "high") {
          color = "#F97316";
          bg = isDarkMode ? "rgba(249, 115, 22, 0.15)" : "#FFF7ED";
          border = "rgba(249, 115, 22, 0.3)";
        } else if (sev === "medium") {
          color = "#F59E0B";
          bg = isDarkMode ? "rgba(245, 158, 11, 0.15)" : "#FEFCE8";
          border = "rgba(245, 158, 11, 0.3)";
        }

        return (
          <Chip
            label={params.value || "N/A"}
            size="small"
            sx={{
              height: "22px",
              fontSize: "11px",
              fontWeight: 700,
              bgcolor: bg,
              color: color,
              border: `1px solid ${border}`,
            }}
          />
        );
      },
    },
    {
      field: "is_active",
      headerName: "Active",
      flex: 0,
      width: 100,
      minWidth: 90,
      maxWidth: 110,
      cellRenderer: (params: any) => {
        const active = getIsActive(params.data);
        return (
          <Chip
            label={active ? "Yes" : "No"}
            size="small"
            sx={{
              height: "22px",
              fontSize: "11px",
              fontWeight: 600,
              bgcolor: active
                ? (isDarkMode ? "rgba(34, 197, 94, 0.12)" : "#F0FDF4")
                : (isDarkMode ? "rgba(107, 114, 128, 0.12)" : "#F3F4F6"),
              color: active ? "#16A34A" : (isDarkMode ? "#9CA3AF" : "#6B7280"),
              border: `1px solid ${active ? "rgba(34, 197, 94, 0.3)" : "rgba(107, 114, 128, 0.3)"}`,
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      flex: 0,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params: any) => (
        <RuleActionCell
          params={params}
          isDarkMode={isDarkMode}
          onEditRule={handleOpenEditRuleModal}
        />
      ),
    },
  ],
  [isDarkMode]
);

  const handleVendorSelectChange = (vendorVal: string) => {
    const foundVendor = vendorsList.find((v) => v.id === vendorVal || v.name === vendorVal);
    if (foundVendor) {
      setSelectedVendorId(foundVendor.id);
      setFormVendor(foundVendor.name);
      setSelectedDeviceModelId("");
      setFormCategory("");
    } else {
      setFormVendor(vendorVal);
      setSelectedVendorId(vendorVal);
      setSelectedDeviceModelId("");
      setFormCategory("");
    }
  };

  const handleCategorySelectChange = (categoryVal: string) => {
    const foundCategory = deviceModelsList.find((m: any) => m.id === categoryVal || m.name === categoryVal);
    if (foundCategory) {
      setSelectedDeviceModelId(foundCategory.id || categoryVal);
      setFormCategory(foundCategory.name || categoryVal);
    } else {
      setSelectedDeviceModelId(categoryVal);
      setFormCategory(categoryVal);
    }
  };

  const handleOpenModal = () => {
    setFormName("");
    setFormDescription("");
    setFormVendor("");
    setSelectedVendorId("");
    setSelectedDeviceModelId("");
    setFormConnecter("Master Config File");
    setFormCategory("");
    setModalFile(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Validation: file upload allowed only if vendor and category are filled
  const isUploadAllowed = Boolean(
    (selectedVendorId || formVendor) &&
    (selectedDeviceModelId || formCategory)
  );

  const handleFileSelect = (file: File) => {
    if (!file || !isUploadAllowed) return;
    setModalFile(file);
  };

  const handleModalFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isUploadAllowed) return;
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleModalDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOverModal(false);
    if (!isUploadAllowed) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleModalDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isUploadAllowed) return;
    setIsDragOverModal(true);
  };

  const handleModalDragLeave = () => {
    setIsDragOverModal(false);
  };

  const triggerModalFileInput = () => {
    if (!isUploadAllowed) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleDownloadSample = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!selectedVendorId) {
      toast.warn("Please select a Vendor first to download the sample file");
      return;
    }
    if (!selectedDeviceModelId) {
      toast.warn("Please select a Category first to download the sample file");
      return;
    }

    setIsDownloadingSample(true);
    try {
      const endpoint = `${END_POINTS.DOWNLOAD_SAMPLE_FILE || "securityscan/sample-file-download"}?file_type=master_template&vendor_id=${selectedVendorId}&category_id=${selectedDeviceModelId}`;

      const response = await axiosInstance.get(endpoint, {
        responseType: "blob",
      });

      if (response?.data) {
        let fileName = "master_config_sample.txt";
        const disposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
        if (disposition && disposition.includes("filename=")) {
          const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1].replace(/['"]/g, "").trim();
          }
        } else {
          const vendorName = (formVendor || "master_config").toLowerCase().replace(/\s+/g, "_");
          const categoryName = (formCategory || "template").toLowerCase().replace(/\s+/g, "_");
          fileName = `${vendorName}_${categoryName}_sample.txt`;
        }

        const blob = new Blob([response.data], {
          type: response.headers?.["content-type"] || "text/plain",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        toast.success("Sample file downloaded successfully");
      } else {
        toast.error("Failed to download sample file");
      }
    } catch (error: any) {
      console.error("Error downloading sample file:", error);
      if (error?.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          toast.error(errorJson.message || "Failed to download sample file");
        } catch {
          toast.error("Failed to download sample file");
        }
      } else {
        toast.error(error?.response?.data?.message || error?.message || "Failed to download sample file");
      }
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const isConnectDisabled =
    !formName.trim() ||
    !isUploadAllowed ||
    !modalFile ||
    isUploading;

  const handleConnectSubmit = async () => {
    if (!formName.trim()) {
      toast.error("Please enter a Name");
      return;
    }
    if (!selectedVendorId && !formVendor) {
      toast.error("Please select a Vendor");
      return;
    }
    if (!selectedDeviceModelId) {
      toast.error("Please select a Category");
      return;
    }
    if (!modalFile) {
      toast.error("Please select a file to upload");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", modalFile);
      formData.append("name", formName.trim());

      if (selectedVendorId) {
        formData.append("vendor_id", selectedVendorId);
      }
      if (selectedDeviceModelId) {
        formData.append("device_model_id", selectedDeviceModelId);
      }

      const endpoint = END_POINTS.MASTER_TEMPLATE_UPLOAD || "securityscan/master-template/upload";
      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        setOpenModal(false);
        toast.success(response.data?.message || "Master config template uploaded successfully!");
        fetchTemplates();
      } else {
        toast.error(response.data?.message || "Failed to upload master config file");
      }
    } catch (error: any) {
      console.error("Error uploading master config template:", error);
      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload master config template"
      );
    } finally {
      setIsUploading(false);
    }
  };



  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Form field styles matching ScansV2 / NewScan layout
  const formFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "13.5px",
      color: isDarkMode ? "#E0E0F0" : "#111827",
      bgcolor: isDarkMode ? "#111827" : "#FFFFFF",
      "& fieldset": {
        borderColor: isDarkMode ? "#374151" : "#D0D5DD",
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "#4B5563" : "#9CA3AF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3A2ADA",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      color: isDarkMode ? "#9CA3AF" : "#667085",
      fontSize: "13.5px",
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
      px: "4px",
      borderRadius: "4px",
      zIndex: 2,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: isDarkMode ? "#7B73E8" : "#3A2ADA",
    },
  };

  const formSelectSx = {
    borderRadius: "8px",
    fontSize: "13.5px",
    color: isDarkMode ? "#E0E0F0" : "#111827",
    bgcolor: isDarkMode ? "#111827" : "#FFFFFF",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "#374151" : "#D0D5DD",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDarkMode ? "#4B5563" : "#9CA3AF",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3A2ADA",
    },
    "& .MuiSvgIcon-root": {
      color: isDarkMode ? "#9CA3AF" : "#667085",
    },
  };

  const customScrollbarSx = {
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#d1d5db",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: isDarkMode ? "rgba(255, 255, 255, 0.35)" : "#9ca3af",
    },
    scrollbarWidth: "thin",
    scrollbarColor: `${isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#d1d5db"} ${isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f1f1"}`,
  };

  const hideScrollbarSx = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0,
    },
  };

  const getFormattedTemplateContent = (details: any): string => {
    if (!details) return "";

    const rawString =
      details.content ??
      details.file_content ??
      details.template_content ??
      details.raw_content;

    if (typeof rawString === "string" && rawString.trim().length > 0) {
      return rawString;
    }

    const rulesList = Array.isArray(details.rules) && details.rules.length > 0
      ? details.rules
      : (Array.isArray(details.custom_rules) && details.custom_rules.length > 0 ? details.custom_rules : null);

    if (rulesList) {
      return rulesList
        .map((r: any) => typeof r === "string" ? r : (r.rule_name || r.parent_cmd || r.name || ""))
        .filter(Boolean)
        .join("\n");
    }

    return JSON.stringify(details, null, 2);
  };

  const showEmptyState = !isLoadingTemplates && templatesList.length === 0;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "calc(100vh - 120px)",
        borderRadius: 3,
        backgroundColor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
        borderColor: isDarkMode ? "#2E323F" : "#E4E7EC",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Hidden File Input used by Upload modal */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.xml"
        disabled={!isUploadAllowed}
        style={{ display: "none" }}
        onChange={handleModalFileChange}
      />

      {isLoadingTemplates && templatesList.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#3A2ADA" }} />
        </Box>
      ) : showEmptyState ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            height: "100%",
            width: "100%",
            p: 3,
            mt: { xs: -1, md: -5 },
          }}
        >
          {/* Centered Illustration SVG */}
          <Box
            component="img"
            src={masterConfigIllustration}
            alt="Upload Master Config"
            sx={{
              width: { xs: 190, sm: 220, md: 250 },
              height: "auto",
              mb: 0,
              userSelect: "none",
            }}
          />

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "18px", sm: "20px" },
              color: isDarkMode ? "#FFFFFF" : "#101828",
              mt: -1,
              mb: 0.75,
              fontFamily: "inherit",
            }}
          >
            Upload the Master Config
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{
              color: isDarkMode ? "#9CA3AF" : "#667085",
              fontSize: "13px",
              lineHeight: 1.5,
              maxWidth: "460px",
              mb: 2,
              fontFamily: "inherit",
            }}
          >
            Upload a master configuration file to create reusable device configuration rules.
          </Typography>

          {/* Upload Button opening Modal */}
          <Button
            variant="contained"
            onClick={handleOpenModal}
            sx={{
              backgroundColor: "#3A2ADA",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              height: "34px",
              px: 2.5,
              borderRadius: "6px",
              boxShadow: "0px 2px 6px rgba(58, 42, 218, 0.2)",
              "&:hover": {
                backgroundColor: "#2E21B6",
                boxShadow: "0px 4px 10px rgba(58, 42, 218, 0.3)",
              },
            }}
          >
            Click to Upload
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mx: 2,
              mt: 1.5,
              mb: 1,
              px: 1.25,
              py: 1.25,
              flexShrink: 0,
              borderRadius: "12px",
              border: `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`,
              bgcolor: isDarkMode ? "#141414" : "#FCFCFD",
              boxShadow: isDarkMode ? "none" : "0 1px 2px rgba(16, 24, 40, 0.04)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                gap: 1,
              }}
            >
              {[
                {
                  key: "vendors",
                  icon: <VendorsIcon sx={{ fontSize: 18 }} />,
                  value: templateListStats.vendors,
                  label: "vendors",
                  iconColor: "#0284C7",
                  iconBg: isDarkMode ? "rgba(2, 132, 199, 0.18)" : "#E0F2FE",
                },
                {
                  key: "templates",
                  icon: <TemplatesIcon sx={{ fontSize: 18 }} />,
                  value: templateListStats.total,
                  label: "templates",
                  iconColor: "#3A2ADA",
                  iconBg: isDarkMode ? "rgba(58, 42, 218, 0.18)" : "#EEF2FF",
                },
                {
                  key: "active",
                  icon: <ActiveStatIcon sx={{ fontSize: 18 }} />,
                  value: templateListStats.active,
                  label: "active",
                  iconColor: "#16A34A",
                  iconBg: isDarkMode ? "rgba(22, 163, 74, 0.18)" : "#ECFDF3",
                },
                {
                  key: "inactive",
                  icon: <InactiveStatIcon sx={{ fontSize: 18 }} />,
                  value: templateListStats.inactive,
                  label: "inactive",
                  iconColor: isDarkMode ? "#9CA3AF" : "#667085",
                  iconBg: isDarkMode ? "rgba(156, 163, 175, 0.15)" : "#F2F4F7",
                },
              ].map((stat) => (
                <Box
                  key={stat.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: "10px",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.04)" : "#F8FAFC",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      bgcolor: stat.iconBg,
                      color: stat.iconColor,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 800,
                        color: isDarkMode ? "#F3F4F6" : "#101828",
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: isDarkMode ? "#9CA3AF" : "#667085",
                        lineHeight: 1.2,
                        textTransform: "capitalize",
                        mt: 0.25,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: isDarkMode ? "#2E323F" : "#E4E7EC", mx: 0.5 }}
            />

            <Button
              variant="contained"
              startIcon={<FileUploadIcon sx={{ fontSize: 18 }} />}
              onClick={handleOpenModal}
              disabled={isUploading}
              sx={{
                backgroundColor: "#3A2ADA",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                px: 2,
                height: "40px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                boxShadow: "none",
                "&:hover": { backgroundColor: "#2E21B6", boxShadow: "none" },
              }}
            >
              Upload template
            </Button>
          </Box>

          <AgGridTable
            columnDefs={columnDefs}
            data={displayedTemplates}
            isLoading={isLoadingTemplates}
            onRowClick={handleRowClick}
            showSearch={true}
            searchPlaceholder="Search templates"
            showAppsIcon={true}
            checkboxSelection={false}
            headerCheckboxSelection={false}
            headerActions={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 1.5,
                  mr: 1,
                  height: 32,
                }}
              >
                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={templateActiveFilter === "all"}
                      width={38}
                      height={20}
                      thumbSize={16}
                      onColor="#3A2ADA"
                      offColor={isDarkMode ? "#4B5563" : "#D1D5DB"}
                      onChange={(event) => {
                        setTemplateActiveFilter(event.target.checked ? "all" : "active");
                      }}
                    />
                  }
                  label="Show all"
                  sx={{
                    m: 0,
                    gap: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "12px",
                      fontWeight: 600,
                      color: isDarkMode ? "#D1D5DB" : "#344054",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Box>
            }
            pagination={true}
            paginationPageSize={10}
            height="calc(100vh - 240px)"
            gridOptions={{
              rowHeight: 56,
            }}
          />
        </Box>
      )}

      {/* Template Details Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 650, md: 900, lg: 960 },
            bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
            p: 3,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            maxHeight: "100vh",
            ...hideScrollbarSx,
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: isDarkMode ? "#FFFFFF" : "#101828",
              }}
            >
              {selectedTemplate?.name || selectedTemplate?.template_name || selectedTemplate?.file_name || "Template Details"}
            </Typography>
          </Box>
          <IconButton onClick={() => setIsDrawerOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs: Summary | Rules | Assets */}
        {(() => {
          const rulesList: any[] = Array.isArray(templateDetails?.rules) && templateDetails.rules.length > 0
            ? templateDetails.rules
            : (Array.isArray(templateDetails?.custom_rules) ? templateDetails.custom_rules : []);
          const rulesCount = templateDetails?.rule_count ?? rulesList.length;

          return (
            <Box sx={{ borderBottom: 1, borderColor: isDarkMode ? "#2E323F" : "#E4E7EC", mb: 2, flexShrink: 0 }}>
              <Tabs
                value={drawerTab}
                onChange={(_, val) => {
                  setDrawerTab(val);
                  if (val === 2) {
                    setAssetsStatusFilter("all");
                  }
                }}
                sx={{
                  minHeight: "38px",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "13.5px",
                    minHeight: "38px",
                    py: 0.5,
                    px: 2,
                    color: isDarkMode ? "#9CA3AF" : "#667085",
                    "&.Mui-selected": {
                      color: "#3A2ADA",
                      fontWeight: 700,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#3A2ADA",
                    height: "2.5px",
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab label="Summary" />
                <Tab
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Rules</span>
                      {rulesCount > 0 && (
                        <Chip
                          label={rulesCount}
                          size="small"
                          sx={{
                            height: "18px",
                            fontSize: "11px",
                            fontWeight: 700,
                            bgcolor: drawerTab === 1 ? "#3A2ADA" : (isDarkMode ? "#374151" : "#E5E7EB"),
                            color: drawerTab === 1 ? "#FFFFFF" : (isDarkMode ? "#E5E7EB" : "#374151"),
                          }}
                        />
                      )}
                    </Box>
                  }
                />
                <Tab label="Assets" />
              </Tabs>
            </Box>
          );
        })()}

        {isLoadingDetails ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexGrow: 1 }}>
            <CircularProgress size={36} sx={{ color: "#3A2ADA" }} />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: drawerTab === 0 ? "auto" : "hidden",
              pr: drawerTab === 0 ? 0.5 : 0,
              ...customScrollbarSx,
            }}
          >
            {/* TAB 0: SUMMARY */}
            {drawerTab === 0 && (
              <Box>
                {/* Metadata Summary — same fields/order as main table */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(4, minmax(0, 1fr))",
                    },
                    columnGap: 3,
                    rowGap: 2.5,
                    px: 0,
                    py: 1.5,
                    mb: 2.5,
                    borderBottom: `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Upload ID
                    </Typography>
                    {(() => {
                      const fullId = templateDetails?.template_id || selectedTemplate?.template_id || "";
                      const shortId = fullId && fullId.length > 8 ? fullId.substring(0, 8) : (fullId || "-");
                      return (
                        <Tooltip title={fullId || ""} arrow placement="top-start">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? "#FFFFFF" : "#101828",
                              fontSize: "13px",
                              cursor: fullId ? "default" : "inherit",
                            }}
                          >
                            {shortId}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Template Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? "#FFFFFF" : "#101828", wordBreak: "break-all", fontSize: "13px" }}>
                      {templateDetails?.name || selectedTemplate?.name || templateDetails?.template_name || selectedTemplate?.template_name || templateDetails?.file_name || selectedTemplate?.file_name || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Vendor
                    </Typography>
                    {(() => {
                      const vendorName = templateDetails?.vendor_name || selectedTemplate?.vendor_name || "";
                      const logo = getVendorLogo(vendorName, isDarkMode);
                      if (logo) {
                        return (
                          <Box sx={{ display: "flex", alignItems: "center", minHeight: 28 }} title={vendorName}>
                            <img
                              src={logo.src}
                              alt={logo.alt}
                              style={{
                                maxHeight: 28,
                                maxWidth: 90,
                                objectFit: "contain",
                                objectPosition: "left center",
                                ...(logo.style || {}),
                              }}
                            />
                          </Box>
                        );
                      }
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? "#FFFFFF" : "#101828", fontSize: "13px" }}>
                          {vendorName || "-"}
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      OS
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? "#FFFFFF" : "#101828", fontSize: "13px" }}>
                      {templateDetails?.device_model_name || selectedTemplate?.device_model_name || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Upload Date & Time
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isDarkMode ? "#FFFFFF" : "#101828", fontSize: "13px" }}>
                      {selectedTemplate?.date_added || templateDetails?.date_added
                        ? new Date(selectedTemplate?.date_added || templateDetails?.date_added).toLocaleString("en-GB", {
                            day: "2-digit", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit", second: "2-digit"
                          })
                        : "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Upload Status
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <UploadStatusBadge
                        label={templateDetails?.status_label || selectedTemplate?.status_label}
                        isDarkMode={isDarkMode}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: isDarkMode ? "#9CA3AF" : "#667085", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Active
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      {(() => {
                        const active = getTemplateActive(templateDetails || selectedTemplate);
                        return (
                          <Chip
                            label={active ? "Yes" : "No"}
                            size="small"
                            sx={{
                              height: "22px",
                              fontSize: "11px",
                              fontWeight: 600,
                              bgcolor: active
                                ? (isDarkMode ? "rgba(34, 197, 94, 0.12)" : "#F0FDF4")
                                : (isDarkMode ? "rgba(107, 114, 128, 0.12)" : "#F3F4F6"),
                              color: active ? "#16A34A" : (isDarkMode ? "#9CA3AF" : "#6B7280"),
                              border: `1px solid ${active ? "rgba(34, 197, 94, 0.3)" : "rgba(107, 114, 128, 0.3)"}`,
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </Box>
                </Box>

                {/* Description if available */}
                {(templateDetails?.description || selectedTemplate?.description) && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: isDarkMode ? "#FFFFFF" : "#101828" }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDarkMode ? "#D0D0E0" : "#475467", lineHeight: 1.6 }}>
                      {templateDetails?.description || selectedTemplate?.description}
                    </Typography>
                  </Box>
                )}

                {/* Raw Template Content */}
                {(() => {
                  const formattedContent = getFormattedTemplateContent(templateDetails);
                  return (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: isDarkMode ? "#FFFFFF" : "#101828" }}>
                          Template Content
                        </Typography>
                        <Tooltip title={copied ? "Copied!" : "Copy Content"}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CopyIcon fontSize="small" />}
                            onClick={() => {
                              navigator.clipboard.writeText(formattedContent);
                              setCopied(true);
                              setTimeout(() => setCopied(false), 2000);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              height: "28px",
                              borderColor: isDarkMode ? "#374151" : "#D0D5DD",
                              color: isDarkMode ? "#E5E7EB" : "#374151",
                            }}
                          >
                            {copied ? "Copied!" : "Copy Content"}
                          </Button>
                        </Tooltip>
                      </Box>
                      <Box
                        component="pre"
                        sx={{
                          p: 2,
                          borderRadius: "8px",
                          bgcolor: isDarkMode ? "#111827" : "#F8FAFC",
                          color: isDarkMode ? "#E5E7EB" : "#1F2937",
                          fontSize: "12px",
                          fontFamily: "'Fira Code', 'Roboto Mono', monospace",
                          overflowX: "auto",
                          maxHeight: "calc(100vh - 430px)",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`,
                          lineHeight: 1.6,
                          ...customScrollbarSx,
                        }}
                      >
                        {formattedContent || "No content available."}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            )}

            {/* TAB 1: RULES */}
            {drawerTab === 1 && (
              <Box sx={{ flex: 1, minHeight: 0, height: "100%", width: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                {(() => {
                  const rulesList: any[] = Array.isArray(templateDetails?.rules) && templateDetails.rules.length > 0
                    ? templateDetails.rules
                    : (Array.isArray(templateDetails?.custom_rules) ? templateDetails.custom_rules : []);

                  return (
                    <AgGridTable
                      columnDefs={rulesColumnDefs}
                      data={rulesList}
                      gridOptions={{ defaultColDef: rulesDefaultColDef }}
                      showSearch={true}
                      searchPlaceholder="Search Rule"
                      pagination={true}
                      paginationPageSize={20}
                      height="100%"
                    />
                  );
                })()}
              </Box>
            )}

            {/* TAB 2: DEVICES */}
            {drawerTab === 2 && (
              <Box sx={{ flex: 1, minHeight: 0, height: "100%", width: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <MasterConfigDevicesTab
                  templateId={String(
                    selectedTemplate?.template_id ||
                      selectedTemplate?.id ||
                      templateDetails?.template_id ||
                      ""
                  )}
                  templateName={getTemplateDisplayName(selectedTemplate || templateDetails)}
                  vendorName={
                    selectedTemplate?.vendor_name || templateDetails?.vendor_name || ""
                  }
                  deviceModelName={
                    selectedTemplate?.device_model_name ||
                    templateDetails?.device_model_name ||
                    ""
                  }
                  isDarkMode={isDarkMode}
                  mappingEnabled={canMapAssetsToTemplate(
                    templateDetails || selectedTemplate
                  )}
                  initialStatusFilter={assetsStatusFilter}
                  templateNamesById={templateNamesById}
                  onDevicesStatsChange={({ mapped }) => {
                    const tid = String(
                      selectedTemplate?.template_id || selectedTemplate?.id || ""
                    );
                    if (tid) {
                      setMappedCountByTemplate((prev) => ({ ...prev, [tid]: mapped }));
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Drawer>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: "600px",
            maxWidth: "95vw",
            borderRadius: "14px",
            p: 0.5,
            bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        {/* Modal Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 0.5,
            pt: 2,
            px: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "18px",
              color: isDarkMode ? "#FFFFFF" : "#101828",
            }}
          >
            Upload Master Config File
          </Typography>
          <IconButton onClick={handleCloseModal} size="small" sx={{ color: isDarkMode ? "#9CA3AF" : "#98A2B3" }}>
            <CancelIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "16px !important", pb: 1.5, overflowY: "auto" }}>
          {/* Form Fields - Compact Grid with proper title distance */}
          <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
            {/* Row 1: Name & Description */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name *"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                size="small"
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                size="small"
                variant="outlined"
                sx={formFieldSx}
              />
            </Grid>

            {/* Row 2: Vendor & Connector / Device Model */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" variant="outlined" sx={formFieldSx} required>
                <InputLabel id="vendor-select-label" required>Vendor</InputLabel>
                <Select
                  labelId="vendor-select-label"
                  value={selectedVendorId || formVendor}
                  onChange={(e) => handleVendorSelectChange(e.target.value)}
                  label="Vendor *"
                  sx={formSelectSx}
                >
                  {vendorsList.map((v) => (
                    <MenuItem key={v.id || v.name} value={v.id || v.name}>
                      {v.name}
                    </MenuItem>
                  ))}
                  {vendorsList.length === 0 && (
                    <MenuItem disabled value="">
                      Loading vendors...
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Connector"
                value={formConnecter}
                onChange={(e) => setFormConnecter(e.target.value)}
                size="small"
                variant="outlined"
                sx={formFieldSx}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* Row 3: Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" variant="outlined" sx={formFieldSx} required>
                <InputLabel id="category-select-label" required>Category</InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedDeviceModelId || formCategory}
                  onChange={(e) => handleCategorySelectChange(e.target.value)}
                  label="Category *"
                  sx={formSelectSx}
                  disabled={!selectedVendorId && !formVendor}
                >
                  {(!selectedVendorId && !formVendor) ? (
                    <MenuItem disabled value="">
                      Select Vendor first
                    </MenuItem>
                  ) : deviceModelsList.length > 0 ? (
                    deviceModelsList.map((m: any) => (
                      <MenuItem key={m.id || m.name} value={m.id || m.name}>
                        {m.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No categories found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Compact Dashed File Upload Box */}
          <Box
            onClick={triggerModalFileInput}
            onDrop={handleModalDrop}
            onDragOver={handleModalDragOver}
            onDragLeave={handleModalDragLeave}
            sx={{
              border: `1.5px dashed ${
                !isUploadAllowed
                  ? isDarkMode
                    ? "#2D3748"
                    : "#E2E8F0"
                  : isDragOverModal
                  ? "#3A2ADA"
                  : isDarkMode
                  ? "#374151"
                  : "#D0D5DD"
              }`,
              borderRadius: "10px",
              py: 2.2,
              px: 2.5,
              textAlign: "center",
              bgcolor: !isUploadAllowed
                ? isDarkMode
                  ? "#141414"
                  : "#FAFAFA"
                : isDarkMode
                ? "#141414"
                : "#F8FAFC",
              opacity: !isUploadAllowed ? 0.6 : 1,
              cursor: isUploadAllowed ? "pointer" : "not-allowed",
              transition: "all 0.2s ease-in-out",
              "&:hover": isUploadAllowed
                ? {
                    borderColor: "#3A2ADA",
                    bgcolor: isDarkMode ? "rgba(58, 42, 218, 0.08)" : "#F0F4FF",
                  }
                : {},
            }}
          >
            {/* Scans V2 Upload Icon */}
            <Box
              component="img"
              src={isDarkMode ? UploadImgDark : UploadImg}
              alt="Upload Icon"
              sx={{
                width: 52,
                height: 52,
                mb: 0.75,
                userSelect: "none",
                opacity: !isUploadAllowed ? 0.45 : 1,
                filter: !isUploadAllowed ? "grayscale(80%)" : "none",
              }}
            />

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                fontSize: "14px",
                color: !isUploadAllowed
                  ? isDarkMode
                    ? "#6B7280"
                    : "#9CA3AF"
                  : isDarkMode
                  ? "#FFFFFF"
                  : "#101828",
                mb: 0.25,
              }}
            >
              Upload your files
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? "#9CA3AF" : "#667085",
                fontSize: "12.5px",
                mb: 0.25,
              }}
            >
              Drag and drop your files here or{" "}
              <Box
                component="span"
                sx={{
                  color: !isUploadAllowed
                    ? isDarkMode
                      ? "#6B7280"
                      : "#9CA3AF"
                    : "#3A2ADA",
                  fontWeight: 600,
                  cursor: isUploadAllowed ? "pointer" : "not-allowed",
                  "&:hover": isUploadAllowed ? { textDecoration: "underline" } : {},
                }}
              >
                choose files
              </Box>
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? "#6B7280" : "#98A2B3",
                display: "block",
                fontSize: "11.5px",
                mb: 0.75,
              }}
            >
              (Supported file formats - .TXT, .XML)
            </Typography>

            {modalFile && (
              <Chip
                icon={<FileIcon fontSize="small" />}
                label={`${modalFile.name} (${formatBytes(modalFile.size)})`}
                onClick={(e) => e.stopPropagation()}
                onDelete={(e) => {
                  e.stopPropagation();
                  setModalFile(null);
                }}
                size="small"
                sx={{
                  bgcolor: isDarkMode ? "rgba(58, 42, 218, 0.15)" : "#EEF2FF",
                  color: "#3A2ADA",
                  fontWeight: 600,
                  mb: 0.75,
                }}
              />
            )}

            <Box>
              <Typography
                variant="body2"
                onClick={!isDownloadingSample ? handleDownloadSample : undefined}
                sx={{
                  color: isDownloadingSample ? (isDarkMode ? "#6B7280" : "#9CA3AF") : "#3A2ADA",
                  fontWeight: 600,
                  fontSize: "12.5px",
                  cursor: isDownloadingSample ? "default" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  "&:hover": !isDownloadingSample ? { textDecoration: "underline" } : {},
                }}
              >
                {isDownloadingSample && <CircularProgress size={12} sx={{ color: "#3A2ADA" }} />}
                {isDownloadingSample ? "Downloading sample file..." : "Download sample file"}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        {/* Modal Actions */}
        <DialogActions sx={{ px: 3, pt: 1, pb: 2, justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            onClick={handleConnectSubmit}
            disabled={isConnectDisabled}
            sx={{
              backgroundColor: "#3A2ADA",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13.5px",
              px: 3.5,
              py: 0.75,
              borderRadius: "6px",
              boxShadow: "0px 2px 6px rgba(58, 42, 218, 0.25)",
              "&:hover": {
                backgroundColor: "#2E21B6",
                boxShadow: "0px 4px 12px rgba(58, 42, 218, 0.35)",
              },
              "&.Mui-disabled": {
                backgroundColor: isDarkMode ? "#374151" : "#E5E7EB",
                color: isDarkMode ? "#6B7280" : "#9CA3AF",
                boxShadow: "none",
                cursor: "not-allowed",
              },
            }}
          >
            {isUploading ? (
              <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
            ) : (
              "Connect"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog
        open={isEditRuleOpen}
        onClose={handleCloseEditRuleModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            p: 1,
            bgcolor: isDarkMode ? "#1A1A1A" : "#FFFFFF",
            boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            pt: 1.5,
            px: 2.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: "17px",
              color: isDarkMode ? "#FFFFFF" : "#101828",
            }}
          >
            Edit Rule
          </Typography>
          <IconButton onClick={handleCloseEditRuleModal} size="small" sx={{ color: isDarkMode ? "#9CA3AF" : "#98A2B3" }}>
            <CancelIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: "12px !important", pb: 1.5 }}>
          {editingRule && (
            <Typography
              variant="body2"
              sx={{
                color: isDarkMode ? "#9CA3AF" : "#667085",
                fontSize: "13px",
                fontWeight: 600,
                mb: 2.5,
                wordBreak: "break-word",
              }}
            >
              {editingRule.rule_name || editingRule.parent_cmd || editingRule.rule_id || "Rule Details"}
            </Typography>
          )}

          <Grid container spacing={2.5}>
            {/* Score Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Score (0 - 10)"
                type="number"
                value={editScore}
                onChange={(e) => setEditScore(e.target.value)}
                size="small"
                variant="outlined"
                inputProps={{ min: 0, max: 10, step: 0.1 }}
                sx={formFieldSx}
              />
            </Grid>

            {/* Severity Dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="outlined" sx={formFieldSx}>
                <InputLabel id="edit-severity-label">Severity</InputLabel>
                <Select
                  labelId="edit-severity-label"
                  value={editSeverity}
                  onChange={(e) => setEditSeverity(Number(e.target.value))}
                  label="Severity"
                  sx={formSelectSx}
                >
                  <MenuItem value={1}>Critical</MenuItem>
                  <MenuItem value={2}>High</MenuItem>
                  <MenuItem value={3}>Medium</MenuItem>
                  <MenuItem value={4}>Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status Yes/No toggle */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#333" : "#E5E7EB"}`,
                  bgcolor: isDarkMode ? "rgba(255,255,255,0.03)" : "#FAFAFA",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: isDarkMode ? "#E5E7EB" : "#111827",
                    }}
                  >
                    Status
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "12px",
                      color: isDarkMode ? "#9CA3AF" : "#6B7280",
                      mt: 0.25,
                    }}
                  >
                    {editIsActive ? "Yes — rule is active" : "No — rule is inactive"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IOSSwitch
                    checked={editIsActive}
                    width={38}
                    height={20}
                    thumbSize={16}
                    onColor="#16A34A"
                    offColor="#D1D5DB"
                    onChange={(e) => setEditIsActive(e.target.checked)}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: editIsActive ? "#16A34A" : "#6B7280",
                      minWidth: 24,
                    }}
                  >
                    {editIsActive ? "Yes" : "No"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pt: 1, pb: 1.5, justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseEditRuleModal}
            disabled={isSavingRule}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "13px",
              borderRadius: "6px",
              height: "36px",
              borderColor: isDarkMode ? "#374151" : "#D0D5DD",
              color: isDarkMode ? "#E5E7EB" : "#344054",
              bgcolor: "transparent",
              "&.MuiButton-outlined:hover": {
                borderColor: isDarkMode ? "#6B7280" : "#98A2B3",
                backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "#F9FAFB",
                color: isDarkMode ? "#F3F4F6" : "#344054",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEditRule}
            disabled={isSavingRule}
            sx={{
              backgroundColor: "#3A2ADA",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "13px",
              height: "36px",
              px: 3,
              borderRadius: "6px",
              boxShadow: "0px 2px 6px rgba(58, 42, 218, 0.25)",
              "&:hover": {
                backgroundColor: "#2E21B6",
              },
            }}
          >
            {isSavingRule ? (
              <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Active / Inactive toggle — Spark-styled */}
      <Dialog
        open={Boolean(activeToggleConfirm)}
        onClose={handleCloseActiveToggleConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: isDarkMode ? "#1E1E1E" : "#FFFFFF",
            fontFamily: "Manrope, sans-serif",
            boxShadow: isDarkMode
              ? "0px 16px 40px rgba(0,0,0,0.45)"
              : "0px 16px 40px rgba(58, 42, 218, 0.12)",
            maxWidth: 440,
          },
        }}
      >
        {(() => {
          const isActivating = Boolean(activeToggleConfirm?.nextActive);
          const row = activeToggleConfirm?.row;
          const templateName = getTemplateDisplayName(row);
          const vendorName = row?.vendor_name || "-";
          const categoryName = row?.device_model_name || "-";

          const statusPill = (label: string, active: boolean) => (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.25,
                py: 0.35,
                borderRadius: "999px",
                fontFamily: "Manrope, sans-serif",
                fontSize: "12px",
                fontWeight: 700,
                lineHeight: 1.4,
                bgcolor: active
                  ? isDarkMode
                    ? "rgba(34, 197, 94, 0.15)"
                    : "#F0FDF4"
                  : isDarkMode
                    ? "rgba(255,255,255,0.04)"
                    : "#FFFFFF",
                color: active
                  ? "#16A34A"
                  : isDarkMode
                    ? "#9CA3AF"
                    : "#6B7280",
                border: active
                  ? "1px solid rgba(34, 197, 94, 0.35)"
                  : `1px solid ${isDarkMode ? "#4B5563" : "#D1D5DB"}`,
              }}
            >
              {label}
            </Box>
          );

          const happenItems = isActivating
            ? [
                {
                  ok: true,
                  text: "This config becomes Active. Other templates for the same vendor/category stay Active.",
                },
                {
                  ok: true,
                  text: "Only devices mapped to this template use it on scan; others stay Spark-only or keep their mapping.",
                },
                {
                  ok: true,
                  text: "You can deactivate it anytime from the table.",
                },
              ]
            : [
                {
                  ok: false,
                  text: "This config becomes Inactive. Device mappings for it are cleared (history kept).",
                },
                {
                  ok: true,
                  text: "Existing results and history are kept.",
                },
                {
                  ok: true,
                  text: "You can reactivate it from the table.",
                },
              ];

          return (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  pt: 2.5,
                  pb: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: isActivating
                      ? isDarkMode
                        ? "rgba(58, 42, 218, 0.2)"
                        : "#EEF2FF"
                      : isDarkMode
                        ? "rgba(245, 158, 11, 0.15)"
                        : "#FFF7ED",
                  }}
                >
                  <WarningIcon
                    sx={{
                      fontSize: 22,
                      color: isActivating ? "#3A2ADA" : "#F59E0B",
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    flex: 1,
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 700,
                    fontSize: "18px",
                    lineHeight: 1.3,
                    color: isDarkMode ? "#F9FAFB" : "#111827",
                    minWidth: 0,
                    wordBreak: "break-word",
                  }}
                >
                  {isActivating
                    ? `Activate ${templateName}?`
                    : `Deactivate ${templateName}?`}
                </Typography>

                <IconButton
                  size="small"
                  onClick={handleCloseActiveToggleConfirm}
                  sx={{
                    flexShrink: 0,
                    color: isDarkMode ? "#9CA3AF" : "#6B7280",
                    "&:hover": {
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.06)" : "#F3F4F6",
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <DialogContent sx={{ px: 2.5, pt: 0, pb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 2.5,
                  }}
                >
                  {statusPill(
                    isActivating ? "Inactive" : "Active",
                    !isActivating
                  )}
                  <ArrowForwardIcon
                    sx={{ fontSize: 16, color: isDarkMode ? "#6B7280" : "#9CA3AF" }}
                  />
                  {statusPill(
                    isActivating ? "Active" : "Inactive",
                    isActivating
                  )}
                  <Typography
                    sx={{
                      ml: 0.5,
                      fontFamily: "Manrope, sans-serif",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: isDarkMode ? "#9CA3AF" : "#6B7280",
                    }}
                  >
                    {vendorName} · {categoryName}
                  </Typography>
                </Box>

                {isActivating && (
                  <Box
                    sx={{
                      mb: 2.5,
                      p: 1.5,
                      borderRadius: "10px",
                      border: `1px solid ${isDarkMode ? "#2E323F" : "#E4E7EC"}`,
                      bgcolor: isDarkMode ? "rgba(255,255,255,0.03)" : "#F8FAFC",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#16A34A",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        mb: 0.5,
                      }}
                    >
                      Making Active
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: "Manrope, sans-serif",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: isDarkMode ? "#F3F4F6" : "#101828",
                        wordBreak: "break-word",
                      }}
                    >
                      {templateName}
                    </Typography>
                  </Box>
                )}

                <Typography
                  sx={{
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 700,
                    fontSize: "13px",
                    color: isDarkMode ? "#E5E7EB" : "#111827",
                    mb: 1.25,
                  }}
                >
                  What happens
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
                  {happenItems.map((item) => (
                    <Box
                      key={item.text}
                      sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                    >
                      {item.ok ? (
                        <CheckCircleFilledIcon
                          sx={{ fontSize: 18, color: "#16A34A", mt: "1px" }}
                        />
                      ) : (
                        <CancelFilledIcon
                          sx={{
                            fontSize: 18,
                            color: isActivating ? "#F59E0B" : "#DC2626",
                            mt: "1px",
                          }}
                        />
                      )}
                      <Typography
                        sx={{
                          fontFamily: "Manrope, sans-serif",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: 1.45,
                          color: isDarkMode ? "#D1D5DB" : "#4B5563",
                        }}
                      >
                        {item.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 2.5,
                  pt: 2,
                  pb: 2.5,
                  gap: 1.25,
                  display: "flex",
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleCloseActiveToggleConfirm}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    borderRadius: "10px",
                    py: 1,
                    borderColor: isDarkMode ? "#4B5563" : "#D1D5DB",
                    color: isDarkMode ? "#E5E7EB" : "#111827",
                    bgcolor: isDarkMode ? "transparent" : "#FFFFFF",
                    // Global MuiButton root hover sets purple bg + white text; force readable cancel styles.
                    "&.MuiButton-outlined:hover": {
                      borderColor: isDarkMode ? "#6B7280" : "#9CA3AF",
                      backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "#F9FAFB",
                      color: isDarkMode ? "#F3F4F6" : "#111827",
                    },
                  }}
                >
                  {isActivating ? "Keep inactive" : "Keep active"}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirmToggleTemplateActive}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    borderRadius: "10px",
                    py: 1,
                    bgcolor: "#3A2ADA",
                    color: "#FFFFFF",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#2E21B6",
                      boxShadow: "none",
                    },
                  }}
                >
                  {isActivating ? "Activate config" : "Deactivate config"}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Card>
  );
};

export default MasterConfig;
